import { getCriteriaError } from './getCriteriaError';
import { parseBoolValue } from './parseBoolValue';
import { parseNumValue } from './parseNumValue';
import { SHOP_CRITERIA_ATTRIBUTE_CT_TYPE_BY_NAME } from './shopCriteriaAttributeAllowlist';
import { VARIANT_SCOPES } from './variantScopes';

export type ShopCriteriaRule = {
    attribute: string;
    value: string[];
};

const escapePredicateValue = (value: string): string => value.replaceAll(/\\/g, '\\\\').replaceAll(/"/g, '\\"'); // NOSONAR

/**
 * CT Product Projection Query:
 * - `text` / `set<text>`: `value="…"` (equality / set membership)
 * - `ltext` / `set<ltext>`: `value(en-US="…")` (localized equality / set membership)
 * - `boolean`: `value=true` / `value=false` (unquoted); only `true` / `false` / `yes` / `no`
 * - `number`: `value=4.5` (unquoted); digits with an optional single decimal point
 * Unparseable boolean/number tokens are skipped. If a rule has no parseable
 * values, the whole group is inactive so removing an AND condition cannot
 * broaden the query.
 */
const buildAttributeMatch = (attribute: string, value: string): string | null => {
    const escapedAttribute = escapePredicateValue(attribute);
    const ctType = SHOP_CRITERIA_ATTRIBUTE_CT_TYPE_BY_NAME.get(attribute);

    if (ctType === 'boolean') {
        const bool = parseBoolValue(value);
        if (bool === null) {
            return null;
        }

        return `attributes(name="${escapedAttribute}" and value=${bool})`;
    }

    if (ctType === 'number') {
        const number = parseNumValue(value);
        if (number === null) {
            return null;
        }

        return `attributes(name="${escapedAttribute}" and value=${number})`;
    }

    const escapedValue = escapePredicateValue(value);

    if (ctType === 'ltext' || ctType === 'set<ltext>') {
        return `attributes(name="${escapedAttribute}" and value(en-US="${escapedValue}"))`;
    }

    // text, set<text>, and remaining allowlisted attrs
    return `attributes(name="${escapedAttribute}" and value="${escapedValue}")`;
};

const buildRulePredicate = ({ attribute, value }: ShopCriteriaRule): string | null => {
    const trimmedAttribute = attribute.trim();
    const trimmedValues = value.map(item => item.trim()).filter(Boolean);

    if (!trimmedAttribute) {
        throw new Error('attribute must not be empty');
    }

    if (trimmedValues.length === 0) {
        throw new Error(`attribute "${attribute}" has no values`);
    }

    const ruleError = getCriteriaError(trimmedAttribute, trimmedValues);

    if (ruleError) {
        throw new Error(ruleError);
    }

    const valuePredicates = trimmedValues
        .map(item => buildAttributeMatch(trimmedAttribute, item))
        .filter((predicate): predicate is string => predicate !== null);

    if (valuePredicates.length === 0) {
        return null;
    }

    return valuePredicates.length === 1 ? valuePredicates[0] : `(${valuePredicates.join(' or ')})`;
};

export const buildGroupPredicate = (group: ShopCriteriaRule[]): string | null => {
    if (group.length === 0) {
        return null;
    }

    const rulePredicates = group.map(buildRulePredicate);
    if (rulePredicates.some(predicate => predicate === null)) {
        return null;
    }

    const innerPredicate = rulePredicates.join(' and ');
    const variantPredicates = VARIANT_SCOPES.map(scope => `${scope}(${innerPredicate})`).join(' or ');

    return `(${variantPredicates})`;
};
